import { useState, useEffect, useRef } from "react";
import { VerticalVideoOverlayPlayer } from "../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { categoryObj } from "../../../ynet1280/newYnetTvVerticalDynamic/components/newYnetTvVerticalDynamicComponenta";
import { RylzMediaItem } from "../../../verticalVideoOverlayPlayer/designs/rylzMediaItem/rylzMediaItem";
import { isWcmEditor } from "../../../../../config/serverConfig";
import { addAxiosDateTransformerSettings } from "../../../../../utils/network/axiosDateTransformer";
import { ItemJsonVer2 } from "../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { SiteItemData, SiteVideoData, SiteImageData } from "../../../../interfaces";
import { MediaContentTypes } from "../../../../../constants/contentTypes";
import { getImageUrl } from "../../../../../config/apiUrls";
import { separateHourFromDateAndFormatToString } from "../../../../widgetsUtils";
import * as axios from "axios";
import { VideoBehaiforData } from "../../../../commonDataObjects/interfaces";
import { getImagesSrc } from "../../../../commonComponents/inline/siteSimpleImage";

export type RylzDisplayType = "basic" | "big" | "small" | "mini";

export interface RylzDisplayItem {
    title: string
    posterSrc: string
    promoPath?: string
    duration?: number
    isVideo?: boolean
    labelData?: { name?: string, logoUrl?: string, author?: string, linkData?: any }
}

export interface RylzInitialItemData {
    title?: string
    labelData?: { name?: string, logoUrl?: string, author?: string, linkData?: any }
    mediaData?: Array<{ type?: string, item?: { src?: string, promoUrl?: string, duration?: number } }>
}

export interface RylzComponentConfig {
    display?: string
    title?: string
    isTabVisible?: boolean
    isTitleVisible?: boolean
    isSubTitleVisible?: boolean
    isDateVisible?: boolean
    hideFeedButton?: boolean
    feedButtonTitle?: string
    icon?: string
    backgroundColor?: string
    fontColor?: string
    moreArticlesPosition?: string
    showPrerollAds?: boolean
    videoBehavior?: VideoBehaiforData
}

interface RylzDisplayProps {
    jsonPageId: string
    isLTR?: boolean
    domain?: string
    category?: categoryObj
    languageCode?: string
    imagesDomain?: string
    initialItems?: RylzInitialItemData[]
    initialConfig?: RylzComponentConfig
}

const ROW_DISPLAY_TYPES: RylzDisplayType[] = ["basic", "small", "mini"];

export const RylzDisplay = (props: RylzDisplayProps) => {

    const { jsonPageId, domain, isLTR, languageCode, category, imagesDomain, initialItems, initialConfig } = props
    const mappedInitialItems = (initialItems || []).map(item => mapInitialItemToDisplayItem(item, imagesDomain));
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    const [isPromoInView, setIsPromoInView] = useState(false);
    const [items, setItems] = useState<RylzDisplayItem[]>(mappedInitialItems);
    const [overlayItems, setOverlayItems] = useState<SiteItemData[]>([]);
    const [config] = useState<RylzComponentConfig>(initialConfig || {});
    const containerRef = useRef(null) as any;
    const itemsRowRef = useRef(null) as any;

    const displayType = (config.display || "big") as RylzDisplayType;
    const isRowDisplay = ROW_DISPLAY_TYPES.indexOf(displayType) !== -1;
    const isCompactDisplay = displayType === "small" || displayType === "mini";
    const isTabVisible = config.isTabVisible;
    const tabTitleText = config.title;
    const tabSubTitleText = config.feedButtonTitle;
    const hideTabMoreButton = config.hideFeedButton;
    const tabBackgroundColor = config.backgroundColor;

    useEffect(() => {
        if (!jsonPageId || isWcmEditor()) return;
        const axiosInstance = axios.create(addAxiosDateTransformerSettings());

        axiosInstance.get(`/iphone/json/mobile/v2/page/${jsonPageId}`)
            .then((res: any) => {
                const rylzComponent = res.data.components.find((component) => component.name === "ynetRylz");
                if (!rylzComponent) return;
                const apiItems: ItemJsonVer2[] = rylzComponent.items || [];
                const videoBehavior = rylzComponent.videoBehavior || (initialConfig && initialConfig.videoBehavior);
                setItems(apiItems.map(mapItemToDisplayItem));
                setOverlayItems(apiItems.map(i => mapItemToOverlayItem(i, videoBehavior)));
            })
            .catch(err => console.error("RylzDisplay: error getting rylz data:", err));
    }, [jsonPageId])

    const firstRylzItem = items[0];
    const secondaryRylzItems = items.slice(1, 5);

    const getDisplayedItemsForPromo = (): RylzDisplayItem[] => {
        return isRowDisplay ? items : items.slice(0, 5);
    };

    const getInitialPromoIndex = (): number => {
        const displayedItems = getDisplayedItemsForPromo();
        for (let i = 0; i < displayedItems.length; i++) {
            if (displayedItems[i] && displayedItems[i].promoPath) return i;
        }
        return -1;
    };

    const [currentPromoIndex, setCurrentPromoIndex] = useState(getInitialPromoIndex());

    useEffect(() => {
        if (items.length > 0) {
            setCurrentPromoIndex(getInitialPromoIndex());
        }
    }, [items]);

    const openFeed = () => {
        if (overlayItems.length === 0) return;
        setSelectedVideoIndex(0);
        setIsOverlayOpen(true);
    };

    useEffect(() => {
        if (isWcmEditor()) return;

        const observer = new IntersectionObserver(
            (entries) => {
                setIsPromoInView(entries[0].isIntersecting);
            },
            { threshold: 0.5 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isRowDisplay || currentPromoIndex < 0 || !itemsRowRef.current) return;
        const itemElement = document.getElementById(`rylzRowItem_${currentPromoIndex}`);
        if (!itemElement) return;
        const itemRect = itemElement.getBoundingClientRect();
        const rowRect = itemsRowRef.current.getBoundingClientRect();
        if (itemRect.left < rowRect.left || itemRect.right > rowRect.right) {
            itemsRowRef.current.scrollBy({ behavior: "smooth", left: itemRect.left - rowRect.left - 15 });
        }
    }, [currentPromoIndex]);

    const onPromoVideoEnded = () => {
        const displayedItems = getDisplayedItemsForPromo();
        let nextIndex = -1;
        for (let i = currentPromoIndex + 1; i < displayedItems.length; i++) {
            if (displayedItems[i] && displayedItems[i].promoPath) {
                nextIndex = i;
                break;
            }
        }
        if (nextIndex === -1) {
            nextIndex = getInitialPromoIndex();
        }
        setCurrentPromoIndex(nextIndex);
    };

    const shouldDisplayPromo = (itemIndex: number) => {
        return !isWcmEditor() && isPromoInView && currentPromoIndex === itemIndex && items[itemIndex] && items[itemIndex].promoPath;
    };

    const handleItemClick = (index: number) => {
        if (overlayItems.length === 0) return;
        setSelectedVideoIndex(index);
        setIsOverlayOpen(true);
    }

    const handleOverlayClose = () => {
        setIsOverlayOpen(false);
    }

    const renderLabel = (labelData: RylzDisplayItem["labelData"], isSecondaryItem?: boolean) => {
        if (!labelData) return null;
        if (labelData.author) {
            return (
                <div className="commentary-label">
                    <span className="author-image-wrapper">
                        <img className="author-image" src={labelData.logoUrl} alt={labelData.author} />
                    </span>
                    {!isSecondaryItem && (
                        <div className="commentary-info">
                            <div className="title">פרשנות</div>
                            <div className="author">{labelData.author}</div>
                        </div>
                    )}
                </div>
            );
        }
        if (labelData.logoUrl) return <img className="logo-image" src={labelData.logoUrl} alt={labelData.name || ""} />;
        if (labelData.name && !isSecondaryItem) return <div className="label-text" dangerouslySetInnerHTML={{ __html: labelData.name }}></div>;
        return null;
    }

    const renderVideoDuration = (item: RylzDisplayItem) => {
        if (!item.isVideo || item.duration == null || item.duration <= 0) return null;
        return (
            <div className="item-video-duration">
                <span className="duration-play-icon"></span>
                <span className="duration-text">{formatDuration(item.duration)}</span>
            </div>
        );
    }

    const renderPromoVideo = (item: RylzDisplayItem, index: number) => {
        if (!shouldDisplayPromo(index)) return null;
        return (
            <video
                muted
                autoPlay
                playsInline
                loop={false}
                src={item.promoPath}
                className="promoVideo"
                poster={item.posterSrc}
                onEnded={onPromoVideoEnded}
                onError={onPromoVideoEnded}
            />
        );
    }

    const renderRowItem = (item: RylzDisplayItem, index: number) => {
        const itemClassName = [
            "rylz-row-item",
            `rylz-${displayType}-item`,
            shouldDisplayPromo(index) ? "promo-playing" : "",
        ].filter(Boolean).join(" ");

        return (
            <div className={itemClassName} onClick={() => handleItemClick(index)} id={`rylzRowItem_${index}`} key={index}>
                {item.posterSrc && <img className="rylz-image" src={item.posterSrc} alt={item.title} />}
                {renderPromoVideo(item, index)}

                {isCompactDisplay ?
                    renderVideoDuration(item)
                    :
                    <div className="item-content">
                        {item.labelData && <div className="item-label">{renderLabel(item.labelData)}</div>}
                        {item.title && <div className="item-title" dangerouslySetInnerHTML={{ __html: item.title }}></div>}
                    </div>
                }
            </div>
        );
    }

    if (!firstRylzItem) return null;

    return (
        <div className="rylz-component-mobile-web">
            {isTabVisible &&
                <div className="rylz-component-tab componentaTab" style={{ backgroundColor: tabBackgroundColor }}>
                    <div className="right-side-title">{tabTitleText}</div>

                    {!hideTabMoreButton && tabSubTitleText &&
                        <div className="left-side-title" onClick={openFeed}>
                            <div className="title-text">{tabSubTitleText}</div>

                            <div className="arrow-icon"></div>
                        </div>
                    }
                </div>}

            <div className="latest-rylz-display" ref={containerRef}>
                {isRowDisplay ?
                    <div className={`rylz-items-row rylz-${displayType}-items-row`} ref={itemsRowRef}>
                        {items.map(renderRowItem)}
                    </div>
                    :
                    <>
                        <div className={`rylz-main-item ${shouldDisplayPromo(0) ? "promo-playing" : ""}`} onClick={() => handleItemClick(0)}>
                            {firstRylzItem.posterSrc && <img className="rylz-image" src={firstRylzItem.posterSrc} alt={firstRylzItem.title} />}

                            {renderPromoVideo(firstRylzItem, 0)}

                            <div className="item-content">
                                {firstRylzItem.labelData && <div className="item-label">{renderLabel(firstRylzItem.labelData)}</div>}
                                <div className="item-title" dangerouslySetInnerHTML={{ __html: firstRylzItem.title }}></div>
                            </div>
                        </div>

                        <div className="rylz-secondary-items">
                            {secondaryRylzItems.map((item, index) => {
                                const itemIndex = index + 1;
                                return (
                                    <div className={`rylz-secondary-item ${shouldDisplayPromo(itemIndex) ? "promo-playing" : ""}`} key={index} onClick={() => handleItemClick(itemIndex)}>
                                        {item.posterSrc && <img className="rylz-image" src={item.posterSrc} alt={item.title} />}

                                        {renderPromoVideo(item, itemIndex)}

                                        {item.labelData && <div className="item-label">{renderLabel(item.labelData, true)}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                }

                <VerticalVideoOverlayPlayer
                    isLTR={isLTR}
                    domain={domain}
                    isMobile={true}
                    category={category}
                    isTitleVisible={true}
                    isSubTitleVisible={true}
                    videoItems={overlayItems}
                    languageCode={languageCode}
                    isOverlayOpen={isOverlayOpen}
                    headerLogoClassName="rylz-header-logo"
                    selectedVideoIndex={selectedVideoIndex}
                    onVideoOverlayClose={handleOverlayClose}
                    showPrerollAds={config.showPrerollAds || false}
                    ItemComponent={RylzMediaItem}
                />
            </div>
        </div>
    )
}

RylzDisplay.siteScriptName = "RylzDisplay";

function formatDuration(seconds: number): string {
    const totalSeconds = Math.round(seconds);
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
    const remainingSeconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
}

function isVideoMediaType(type?: string): boolean {
    return type === "video" || type === MediaContentTypes.MEDIA_VIDEO;
}

function mapInitialItemToDisplayItem(item: RylzInitialItemData, imagesDomain: string): RylzDisplayItem {
    const media = item.mediaData && item.mediaData[0];
    const mediaItem = media && media.item || {};
    let posterSrc = mediaItem.src || "";
    const isVideo = isVideoMediaType(media && media.type);

    if (media && media.type === MediaContentTypes.MEDIA_IMAGE) {
        const imageSrc = getImagesSrc(imagesDomain, mediaItem.src)
        posterSrc = getImageUrl({ publishedLink: imageSrc }, { w: 640, h: 1137 });
    }

    return {
        title: item.title || "",
        posterSrc,
        promoPath: mediaItem.promoUrl || "",
        duration: mediaItem.duration,
        isVideo,
        labelData: item.labelData,
    };
}

function mapItemToDisplayItem(item: ItemJsonVer2): RylzDisplayItem {
    const media = item.mediaData && item.mediaData[0];
    let posterSrc = "";
    let promoPath = "";
    let duration: number | undefined;
    let isVideo = false;

    if (media) {
        if (media.type === "video") {
            const videoItem = media.item as any;
            posterSrc = videoItem.opening_img ? videoItem.opening_img.src : (videoItem.src || "");
            promoPath = videoItem.promoUrl || "";
            duration = videoItem.duration;
            isVideo = true;
        } else if (media.type === "image") {
            const imageItem = media.item as any;
            posterSrc = getImageUrl({ publishedLink: imageItem.src }, { w: 640, h: 1137 });
        }
    }

    return {
        title: item.title || "",
        posterSrc,
        promoPath,
        duration,
        isVideo,
        labelData: item.labelData,
    };
}

function mapItemToOverlayItem(item: ItemJsonVer2, videoBehavior?: VideoBehaiforData): SiteItemData {
    const mediaSource = item.mediaData && item.mediaData[0];
    const linkData = item.linkData as any;
    const link = linkData && linkData.link;
    const itemAny = item as any;

    let media: SiteVideoData | SiteImageData;
    if (mediaSource && mediaSource.type === "video") {
        const v = mediaSource.item as any;
        media = {
            mediaType: MediaContentTypes.MEDIA_VIDEO,
            url: v.url || "",
            downGradeUrl: "",
            poster: v.opening_img ? v.opening_img.src : (v.src || ""),
            youtube_id: "",
            blockAbroadViews: false,
            duration: v.duration,
            promoPath: v.promoUrl || "",
            videoType: "vertical_video",
            mediaId: v.videoId ? String(v.videoId) : "",
            aspectRatio: v.mediaProportion,
            isAutoPlaying: v.isAutoPlaying,
            isLoopPlaying: v.isLoopPlaying,
            isPlayerBarVisible: v.isPlayerBarVisible,
            isMuteAvailable: v.isMuteAvailable,
            ...(videoBehavior || {}),
        } as SiteVideoData;
    } else if (mediaSource && mediaSource.type === "image") {
        const img = mediaSource.item as any;
        media = {
            mediaType: MediaContentTypes.MEDIA_IMAGE,
            url: getImageUrl({ publishedLink: img.src }, { w: 640, h: 1137 }),
            alt: img.alt || "",
            title: item.title || "",
            className: "",
            credit: img.credit || "",
        } as SiteImageData;
    }

    const publishedLink = link && link.href ? link.href : "";
    const articleId = link && link.id ? String(link.id) : undefined;
    const labelData = item.labelData || {};
    const author = labelData.author || (link && link.author ? link.author : "");
    const pubDate = link && link.pubDate;
    const dateStr = pubDate ? separateHourFromDateAndFormatToString(pubDate, { showDate: true, showHour: true }) : "";

    return {
        itemId: articleId || "",
        title: item.title || "",
        subTitle: item.subTitle || "",
        titleLink: publishedLink,
        media,
        author,
        publishedLink,
        articleId,
        readMoreTitle: itemAny.buttonTitle || "",
        labelUrl: labelData.logoUrl || "",
        labelName: labelData.name || "",
        labelAuthor: labelData.author || "",
        date: dateStr,
    } as any;
}
